import React, { useEffect, useRef, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { IOptions } from '../../../interface/new-car.interface';
import { IoChevronDown } from 'react-icons/io5';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../store';
import ModalMobileNativeScroll from '../ModalMobileNativeScroll';
import ArrowForward from '../Icons/ArrowForward';

interface Props {
  id?: string;
  label?: string;
  labelClassName?: string;
  options: IOptions[];
  optionLabel?: string;
  placeholder?: string;
  headerModalTitle?: string;
  selectedOption: { value: string; label: string };
  onSelect: (val) => void;
  disabled?: boolean;
  required?: boolean;
}

function ContentModal({
  options,
  selected,
  optionLabel,
  onChange,
  setVisible,
}: {
  options: IOptions[];
  selected: IOptions;
  optionLabel: string;
  onChange: (val: IOptions) => void;
  setVisible: (val: boolean) => void;
}) {
  const [selectedContainer, setSelectedContainer] = React.useState<IOptions>(selected);

  return (
    <div className="h-full">
      <div className="overflow-y-auto max-h-[calc(100vh-156px)]">
        {options.map((option, index) => {
          return (
            <div
              className="py-4 flex gap-4 items-center border-b-[0.5px] px-4"
              onClick={() => setSelectedContainer(option)}
              key={index}
            >
              <input
                type="radio"
                name={`name-${option?.value}-${index}`}
                value={option?.value}
                checked={selectedContainer ? selectedContainer?.value === option?.value : false}
                onChange={() => setSelectedContainer(option)}
                className="h-4 w-4 text-red-500 focus:ring-red-500 cursor-pointer accent-supportiveRed"
              />
              <p className="text-sm font-medium uppercase">{option[optionLabel]}</p>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="fixed bottom-0 left-0 w-full flex gap-2 justify-end disabled:bg-reliableBlack20 bg-supportiveRed items-center p-4 text-white text-base font-bold"
        onClick={() => {
          if (!selectedContainer) return;
          onChange(selectedContainer);
          setVisible(false);
        }}
        disabled={!selectedContainer}
      >
        SIMPAN <ArrowForward color="#ffffff" size={24} />
      </button>
    </div>
  );
}

function DropSelect({
  id = '',
  label = '',
  labelClassName,
  options,
  optionLabel,
  placeholder,
  selectedOption,
  headerModalTitle,
  onSelect,
  required = false,
  disabled = false,
}: Props) {
  const dropdownRef = useRef(null);
  const inputDropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const { isLargeScreen } = useSelector((item: IRootState) => item.page) || {
    isLargeScreen: false,
  };

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [dropdownPosition, setDropdownPosition] = useState<string>('bottom');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        e.target !== inputDropdownRef.current
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!isLargeScreen) {
      setIsModalOpen(false);
    }
  }, [isLargeScreen]);

  useEffect(() => {
    if (isDropdownOpen) {
      calculatePosition();

      // Recalculate on scroll or resize
      const handleResize = () => calculatePosition();
      const handleScroll = () => calculatePosition();

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isDropdownOpen]);

  const handleOptionSelect = (option) => {
    if (option?.value === selectedOption?.value) return;
    onSelect(option);
    setIsDropdownOpen(false);
  };

  const calculatePosition = () => {
    if (!buttonRef.current || !dropdownRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current.offsetHeight;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    // If there's not enough space below but enough above, position above
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      setDropdownPosition('top');
    } else {
      setDropdownPosition('bottom');
    }
  };

  return (
    <>
      <div className="relative">
        {label && (
          <label className={`${labelClassName}`}>
            {label}
            {required && <span className="text-red-500"> *</span>}
          </label>
        )}

        {isLargeScreen && (
          <>
            <div
              ref={buttonRef}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center cursor-pointer"
            >
              <input
                id={id}
                ref={inputDropdownRef}
                type="text"
                value={selectedOption ? selectedOption[optionLabel] : ''}
                className={`w-full outline-none border-b border-reliableBlack30 focus:border-reliableBlack60 px-5 py-3 ${disabled ? 'bg-reliableBlack10 placeholder:text-reliableBlack50 text-reliableBlack50' : 'bg-reliableBlack3 placeholder:text-reliableBlack60 text-reliableBlack cursor-pointer'}`}
                placeholder={placeholder}
                readOnly
                disabled={disabled}
              />

              {/* Handle toggle icon */}
              <span
                className={`absolute right-4 pointer-events-none ${
                  isDropdownOpen
                    ? 'transform rotate-180 transition duration-100 text-reliableBlack80'
                    : 'text-reliableBlack80 transform rotate-0 duration-100'
                }`}
              >
                <IoChevronDown color="#737373" />
              </span>
            </div>

            {isDropdownOpen && (
              <ul
                className={`thin-scrollbar absolute w-full overflow-y-auto z-[49] border border-[#F4F4F4] max-h-[200px] left-0 text-reliableBlack80 bg-white shadow-lg rounded p-6 ${
                  dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
                }`}
                ref={dropdownRef}
              >
                {options?.length ? (
                  options.map((option, index) => (
                    <li
                      id={`dd-form-${String(option[optionLabel]).toLowerCase().replace(/\s/g, '-')}`}
                      key={option['id'] || index}
                      className={`px-6 py-4 cursor-pointer border-b ${option?.value === selectedOption?.value ? 'bg-[#f5f5f5]' : ''}`}
                      onClick={() => handleOptionSelect(option)}
                    >
                      {option[optionLabel]}
                    </li>
                  ))
                ) : (
                  <li className="px-4 py-2 text-center">Tidak ada pilihan</li>
                )}
              </ul>
            )}
          </>
        )}

        {!isLargeScreen && (
          <div
            className={`w-full px-5 py-3 border-b border-reliableBlack30 flex justify-between items-center ${disabled ? 'cursor-not-allowed bg-reliableBlack10' : 'cursor-pointer bg-reliableBlack3'}`}
            onClick={() => {
              if (!disabled) setIsModalOpen(true);
            }}
          >
            <span
              className={`${disabled ? 'text-[#d1d1d1]' : selectedOption?.[optionLabel] ? 'text-reliableBlack font-medium' : 'text-reliableBlack50'}`}
            >
              {selectedOption ? selectedOption[optionLabel] : placeholder}
            </span>
            <FiChevronDown color="#727272" size={18} />
          </div>
        )}
      </div>
      {isModalOpen && !isLargeScreen && (
        <ModalMobileNativeScroll
          title={headerModalTitle}
          modalClassName={isModalOpen ? '!z-100' : ''}
          visible={isModalOpen}
          bodyClassName="flex flex-col justify-between"
          headerClassName="!mb-0"
          titleHeaderClassName="!text-sm !font-semibold !text-reliableBlack"
          height="h-full"
          onClose={() => setIsModalOpen(false)}
        >
          <ContentModal
            options={options}
            selected={selectedOption}
            optionLabel={optionLabel}
            onChange={(option) => handleOptionSelect(option)}
            setVisible={setIsModalOpen}
          />
        </ModalMobileNativeScroll>
      )}
    </>
  );
}

export default DropSelect;
